import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Loader2, Printer, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { CardTitleWithHelp, HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { BatchDetailLink } from "@/components/hub/HubEntityDetailLink";
import { ApiError } from "@/lib/api";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";
import { EM_DASH } from "@/lib/utils";
import { useHubToast } from "@/components/ui/HubToast";
import {
  fetchSuntronicBoxLabelLine,
  fetchSuntronicBoxLabelLines,
  fetchSuntronicBoxLabelPreview,
  printSuntronicBoxLabels,
  reprintSuntronicBoxLabels,
  SUNTRONIC_BOX_LABELS_QUERY_KEY,
  type SuntronicBoxLabelLineRow,
} from "@/pages/manufacturing/suntronicBoxLabelsApi";

const PAGE_HELP =
  "Print hub FI-3C, FI-5A, and FI-5A-W box labels for Suntronic batch lines when device labels are not scanned yet. Default quantity is 102% of the order, rounded up; you can print more. Fully shipped lines are hidden unless you uncheck that filter. Serials use the old all-numeric layout with year 1984 so they stay out of the live device-label namespace.";

const PRINT_PANEL_HELP =
  "Suggested quantity is 102% of the order quantity, rounded up, minus labels already printed. You can type a larger quantity. Serials are registered so other label jobs cannot reuse them.";

type SortKey =
  | "batch_id"
  | "sku"
  | "order_quantity"
  | "issued_count"
  | "remaining_print_quantity"
  | "mfr_ship_by_date";

const BOX_LABEL_LINE_COLUMNS: HubDataTableColumn<SuntronicBoxLabelLineRow, SortKey>[] = [
  {
    id: "batch_id",
    header: "Batch",
    sortKey: "batch_id",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.batch_id,
    cell: (row) => <BatchDetailLink batchId={row.batch_id} />,
  },
  {
    id: "sku",
    header: "SKU",
    sortKey: "sku",
    cellClassName: "font-mono text-xs",
    sortValue: (row) => row.sku,
    cell: (row) => row.sku,
  },
  {
    id: "order_quantity",
    header: "Order quantity",
    sortKey: "order_quantity",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.order_quantity,
    cell: (row) => row.order_quantity,
  },
  {
    id: "issued_count",
    header: "Printed",
    sortKey: "issued_count",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.issued_count,
    cell: (row) => row.issued_count,
  },
  {
    id: "remaining_print_quantity",
    header: "Remaining",
    sortKey: "remaining_print_quantity",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.remaining_print_quantity,
    cell: (row) => row.remaining_print_quantity,
  },
  {
    id: "mfr_ship_by_date",
    header: "Ship by",
    sortKey: "mfr_ship_by_date",
    overflow: "nowrap",
    sortValue: (row) => row.mfr_ship_by_date ?? "",
    cell: (row) => formatDate(row.mfr_ship_by_date),
  },
];

const LIST_AUTO_REFRESH_MS = 60_000;

function formatListLastRefreshedAt(dataUpdatedAt: number): string {
  if (!Number.isFinite(dataUpdatedAt) || dataUpdatedAt <= 0) {
    return "not loaded yet";
  }
  return new Date(dataUpdatedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function previewImageUrl(pngBase64: string): string {
  return `data:image/png;base64,${pngBase64}`;
}

function formatDate(value: string | null): string {
  if (value == null || value.trim() === "") return EM_DASH;
  return value.slice(0, 10);
}

function suggestedPrintQuantity(line: SuntronicBoxLabelLineRow): number {
  if (line.remaining_print_quantity > 0) return line.remaining_print_quantity;
  return line.default_print_quantity > 0 ? line.default_print_quantity : 1;
}

export function SuntronicBoxLabelsPage() {
  const writesAllowed = useManufacturingScopeHubWrite();
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [hideFullyShipped, setHideFullyShipped] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("batch_id");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [quantityInput, setQuantityInput] = useState("");
  const selectedBatchItemId = Number.parseInt(searchParams.get("batch_item_id") ?? "", 10);
  const selectedId =
    Number.isFinite(selectedBatchItemId) && selectedBatchItemId > 0 ? selectedBatchItemId : null;

  const { station, setStation, selectedPrinter, unavailableMessage } = useZplPrintLocation({
    labelSize: "4x6",
    dpi: 300,
    storageKey: ZPL_PRINTER_STORAGE_KEYS.suntronicBoxLabels,
  });

  const listQuery = useQuery({
    queryKey: SUNTRONIC_BOX_LABELS_QUERY_KEY,
    queryFn: fetchSuntronicBoxLabelLines,
    refetchInterval: LIST_AUTO_REFRESH_MS,
  });

  const lineQuery = useQuery({
    queryKey: [...SUNTRONIC_BOX_LABELS_QUERY_KEY, "line", selectedId],
    queryFn: () => fetchSuntronicBoxLabelLine(selectedId!),
    enabled: selectedId != null,
  });

  const previewQuery = useQuery({
    queryKey: [...SUNTRONIC_BOX_LABELS_QUERY_KEY, "preview", selectedId, selectedPrinter],
    queryFn: () => fetchSuntronicBoxLabelPreview({ batchItemId: selectedId!, printer: selectedPrinter }),
    enabled: selectedId != null,
  });

  const selectedLine = lineQuery.data?.line ?? null;

  useEffect(() => {
    if (selectedLine == null) return;
    setQuantityInput(String(suggestedPrintQuantity(selectedLine)));
  }, [selectedLine?.batch_item_id, selectedLine?.remaining_print_quantity, selectedLine?.default_print_quantity]);

  function toggleSort(key: SortKey): void {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "batch_id" ? "desc" : "asc");
  }

  function selectLine(row: SuntronicBoxLabelLineRow): void {
    const next = new URLSearchParams(searchParams);
    next.set("batch_item_id", String(row.batch_item_id));
    setSearchParams(next, { replace: true });
  }

  const searchMatchedLines = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = listQuery.data?.lines ?? [];
    if (needle === "") return rows;
    return rows.filter((row) => {
      const haystack = [
        String(row.batch_id),
        row.sku,
        row.manufacturer_sales_order_number ?? "",
        row.bar_code_model ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [listQuery.data?.lines, search]);

  const filteredLines = useMemo(() => {
    if (!hideFullyShipped) return searchMatchedLines;
    return searchMatchedLines.filter((row) => !row.fully_shipped);
  }, [hideFullyShipped, searchMatchedLines]);

  const printMutation = useMutation({
    mutationFn: () =>
      printSuntronicBoxLabels({
        batchItemId: selectedId!,
        printer: selectedPrinter,
        quantity: Number.parseInt(quantityInput, 10),
      }),
    onSuccess: async (result) => {
      showToast({
        message: `Printed ${String(result.printed_count)} Suntronic box label(s).`,
        tone: "success",
      });
      await queryClient.invalidateQueries({ queryKey: SUNTRONIC_BOX_LABELS_QUERY_KEY });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : error.message,
        tone: "error",
      });
    },
  });

  const reprintMutation = useMutation({
    mutationFn: () =>
      reprintSuntronicBoxLabels({
        batchItemId: selectedId!,
        printer: selectedPrinter,
      }),
    onSuccess: async (result) => {
      showToast({
        message: `Reprinted ${String(result.printed_count)} Suntronic box label(s).`,
        tone: "success",
      });
      await queryClient.invalidateQueries({ queryKey: SUNTRONIC_BOX_LABELS_QUERY_KEY });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : error.message,
        tone: "error",
      });
    },
  });

  const printQuantity = Number.parseInt(quantityInput, 10);
  const printBusy = printMutation.isPending || reprintMutation.isPending;
  const printDisabled =
    !writesAllowed ||
    selectedId == null ||
    selectedPrinter.trim() === "" ||
    !Number.isInteger(printQuantity) ||
    printQuantity <= 0 ||
    selectedLine?.bar_code_model_ready !== true ||
    printBusy;

  const filtersActive = search.trim() !== "" || hideFullyShipped;
  const lineCountTitle = listQuery.isLoading
    ? "Loading Suntronic box-label lines…"
    : searchMatchedLines.length !== filteredLines.length
      ? `Showing ${String(filteredLines.length)} of ${String(searchMatchedLines.length)} lines`
      : `${String(filteredLines.length)} line${filteredLines.length === 1 ? "" : "s"}`;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Suntronic box labels</PageTitleWithHelp>
        <div className="flex flex-wrap items-end gap-3">
          <ZplLocationSelect
            id="suntronic-box-labels-location"
            size="compact"
            value={station}
            onChange={setStation}
            unavailableMessage={unavailableMessage}
          />
          <HoverHintTrigger
            trigger={
              <Button
                type="button"
                variant="secondary"
                disabled={listQuery.isFetching}
                onClick={() => void listQuery.refetch()}
              >
                {listQuery.isFetching ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <RefreshCw className="h-4 w-4" aria-hidden />
                )}
                Refresh
              </Button>
            }
            align="center"
          >
            The batch line list refreshes automatically every{" "}
            {String(Math.round(LIST_AUTO_REFRESH_MS / 1000))} seconds. Last refreshed at{" "}
            {formatListLastRefreshedAt(listQuery.dataUpdatedAt)}. Click for an immediate update.
          </HoverHintTrigger>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div>
              <Label htmlFor="suntronic-box-labels-search">Search</Label>
              <Input
                id="suntronic-box-labels-search"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Batch, SKU, or sales order"
              />
            </div>
            <label className="inline-flex h-10 items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={hideFullyShipped}
                onChange={(event) => setHideFullyShipped(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              Hide fully shipped
            </label>
          </div>
        </CardBody>
      </Card>

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[minmax(22rem,38%)_minmax(0,1fr)] lg:items-stretch">
      <HubDataTable
        title={lineCountTitle}
        columns={BOX_LABEL_LINE_COLUMNS}
        rows={filteredLines}
        rowKey={(row) => row.batch_item_id}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={listQuery.isLoading}
        loadingMessage="Loading Suntronic box-label lines…"
        error={listQuery.isError ? (listQuery.error as Error).message : null}
        empty="No Suntronic hub-box batch lines were found."
        isFiltered={filtersActive}
        filteredEmpty="No rows match these filters."
        onRowClick={(row) => selectLine(row)}
        rowClassName={(row) => (row.batch_item_id === selectedId ? "bg-sky-50" : undefined)}
        chrome="compactHeader"
      />

      <HubViewportCard>
        {selectedId == null ? (
          <CardBody className="flex flex-1 flex-col justify-center">
            <p className="text-sm text-slate-600">Select a line from the list to print box labels.</p>
          </CardBody>
        ) : (
          <>
            <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
              <CardTitleWithHelp hoverHint={PRINT_PANEL_HELP}>Print box labels</CardTitleWithHelp>
              {selectedLine != null ? (
                selectedLine.bar_code_model_ready ? (
                  <Badge tone="success">Model {selectedLine.bar_code_model}</Badge>
                ) : (
                  <Badge tone="warning">No bar code model</Badge>
                )
              ) : lineQuery.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-hidden />
              ) : null}
            </CardHeader>
            <CardBody className="min-h-0 flex-1 overflow-y-auto">
              {lineQuery.isLoading ? <p className="text-sm text-slate-500">Loading line…</p> : null}
              {lineQuery.isError ? (
                <p className="text-sm text-rose-700">{(lineQuery.error as Error).message}</p>
              ) : null}
              {selectedLine != null ? (
                <div className="grid min-h-0 gap-4 lg:grid-cols-[minmax(12rem,18rem)_minmax(0,1fr)] lg:items-start">
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Batch {String(selectedLine.batch_id)} · {selectedLine.sku} · order{" "}
                      {String(selectedLine.order_quantity)} · suggested{" "}
                      {String(selectedLine.default_print_quantity)} (102% rounded up) · printed{" "}
                      {String(selectedLine.issued_count)}
                    </p>
                    {!selectedLine.bar_code_model_ready ? (
                      <p className="text-sm text-amber-800">
                        Assign a bar code model on this SKU in MRP before printing. The serial encodes that
                        three-digit model.
                      </p>
                    ) : null}
                    <div className="w-40">
                      <Label htmlFor="suntronic-box-label-qty">Quantity</Label>
                      <Input
                        id="suntronic-box-label-qty"
                        inputMode="numeric"
                        value={quantityInput}
                        onChange={(event) => setQuantityInput(event.target.value)}
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      Suggested remaining is {String(selectedLine.remaining_print_quantity)}. You can print more
                      than that.
                    </p>
                    {(lineQuery.data?.serials.length ?? 0) > 0 ? (
                      <p className="font-mono text-xs text-slate-500">
                        Printed {lineQuery.data!.serials[0]!.serial_number} through{" "}
                        {lineQuery.data!.serials[lineQuery.data!.serials.length - 1]!.serial_number}
                      </p>
                    ) : null}
                  </div>
                  <div className="min-w-0 space-y-2">
                    {previewQuery.isLoading ? (
                      <p className="text-sm text-slate-500">Loading preview…</p>
                    ) : null}
                    {previewQuery.isError ? (
                      <p className="text-sm text-rose-700">{(previewQuery.error as Error).message}</p>
                    ) : null}
                    {previewQuery.data != null ? (
                      <>
                        <p className="font-mono text-xs text-slate-600">
                          Preview serial {previewQuery.data.serial_number}
                        </p>
                        <img
                          alt={`Box label preview for ${previewQuery.data.serial_number}`}
                          src={previewImageUrl(previewQuery.data.png_base64)}
                          className="h-auto w-full max-h-[min(70vh,40rem)] rounded border border-slate-200 bg-white object-contain"
                        />
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </CardBody>
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-4 py-3">
              <Button
                type="button"
                variant="secondary"
                disabled={
                  !writesAllowed ||
                  selectedPrinter.trim() === "" ||
                  (selectedLine?.issued_count ?? 0) <= 0 ||
                  printBusy
                }
                onClick={() => reprintMutation.mutate()}
              >
                Reprint printed
              </Button>
              <Button type="button" disabled={printDisabled} onClick={() => printMutation.mutate()}>
                {printMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Printer className="h-4 w-4" aria-hidden />
                )}
                Print
              </Button>
            </div>
          </>
        )}
      </HubViewportCard>
      </div>
    </div>
  );
}
